<template>
  <div v-if="!loadingForm">
    <div
      :key="componentKey"
         ref="dynamicFormInput"
         class="text-break" />
  </div>
  <ItemsLoadingSpinner v-if="loadingForm" />
</template>

<script setup lang="ts">
  import { ref, watch, onMounted, nextTick } from 'vue';
  import ItemsLoadingSpinner from '@/VueComponents/Loading/ItemsLoadingSpinner.vue';

  // Dynamic import for the large library
  const loadingForm = ref(true);

  const emit = defineEmits(['formDataBound', 'update:modelValue']);
  defineExpose({
    validate
  });
  const dynamicFormInput = ref(null);
  let dynamicFormInstance: Object | null = null;
  let DynamicFormFactory: any = null;

  const props = defineProps({
    formDefinition: { type: Array, default: () => [] },
    isReadOnly: { type: Boolean },
    i18n: { type: Object, default: () => ({ 'en': [] }) },
    language: { type: String, default: () => 'en' },
    modelValue: { type: Object, required: false, default: () => null },
    disableValidation: { type: Boolean }
  });

  const isReadOnly = ref(props.isReadOnly);

  // Required properties
  watch(isReadOnly, value => {
    forceRerender();
  });

  let componentKey: number = 0;
  let formDataBound: boolean = false;

  onMounted(async () => {
    // Dynamically import the library
    const module = await import('@publicsafety/dynamic-forms');
    DynamicFormFactory = module.default;
    loadingForm.value = false;

    // Wait for DOM to update before rendering form
    await nextTick();
    renderForm();
  });

  async function renderForm() {
    if (!DynamicFormFactory) return;

    // Ensure the container element exists
    if (!dynamicFormInput.value) {
      console.warn('Container element not available for dynamic form rendering');
      return;
    }

    const options = {
      containerElement: dynamicFormInput.value,
      components: props.formDefinition,
      data: props.modelValue,
      isReadOnly: isReadOnly.value,
      isViewAsHtml: isReadOnly.value,
      language: props.language,
      i18n: props.i18n,
      icons: 'fontawesome',
      dataChangedCallback: onDataChanged
    };

    DynamicFormFactory.renderForm(options)
      .then((instance: any) => {
        dynamicFormInstance = instance;
      });
  }

  async function onDataChanged(dataChangedDetails: any): Promise<void> {
    // Validate form manually to highlight validation issues
    let isFormValid = true;

    if (!props.disableValidation) {
      isFormValid = await dynamicFormInstance.validate();
    }

    emit('update:modelValue', dataChangedDetails.data, isFormValid);

    // Raise an event once the model has been bound. When a new form is rendered without any initial data,
    // the model used is an empty object. After the form has loaded, the onDataChanged callback is executed
    // and the model will now be populated with a property (with a default value) for each form field.
    if (!formDataBound) {
      formDataBound = true;
      emit('formDataBound');
    }
  }

  async function forceRerender(): Promise<void> {
    // DynamicForm component doesn't provide an API to rerender component as a readonly therefore the following workaround is used.
    // https://michaelnthiessen.com/force-re-render/
    componentKey += 1;
    await nextTick();
    renderForm();
  }

  async function validate() {
    if (!dynamicFormInstance) return false;
    return await dynamicFormInstance.validate();
  }
</script>